import { memo } from "react";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import styles from "./ContentCard.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";

export interface ContentCardItem {
  id: number;
  name: string;
  slug: string;
  mainImage?: {
    url?: string;
    alt?: string;
  };
  category?: {
    name?: string;
    slug?: string;
  };
  readTime?: string;
  date?: string;
  updatedAt?: string;
}

interface ContentCardProps {
  item: ContentCardItem;
  basePath: string;
  linkTo?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const ContentCard = ({ item, basePath, linkTo }: ContentCardProps) => {
  const isDesktop = useIsDesktop();

  const imageUrl = item.mainImage?.url || "/images/blog-placeholder.png";
  const imageAlt = item.mainImage?.alt || item.name;
  const categoryName = item.category?.name || "Article";
  const readTime = item.readTime || "5 Min";
  const itemDate =
    basePath === "/news"
      ? item.date || item.updatedAt || new Date().toISOString()
      : item.updatedAt || item.date || new Date().toISOString();

  return (
    <CustomLink
      to={linkTo || `${basePath}/post/${item.slug}`}
      redirect={isDesktop}
      className={styles.contentCard}
      isExternal
    >
      <div className={styles.imageContainer}>
        <Image
          src={`${imageUrl}?w=500`}
          alt={imageAlt}
          width="296"
          height="171"
          className={styles.cardImage}
        />
      </div>
      <div className={styles.cardContent}>
        <div className={styles.metaRow}>
          <span className={styles.categoryBadge}>{categoryName}</span>
          <div className={styles.metaInfo}>
            <span className={styles.readTime}>{readTime}</span>
            <span className={styles.separator}>·</span>
            <span className={styles.date}>{formatDate(itemDate)}</span>
          </div>
        </div>
        <h3 className={styles.cardTitle}>{item.name}</h3>
      </div>
    </CustomLink>
  );
};

export default wrapperHOC(memo(ContentCard), {
  componentName: "ContentCard",
  showForChina: true,
});
